import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { getBrandLogo, isAlphaFlashHost } from '@/shared/theme/export';
import { NotificationBell } from '@/core/notifications/export';
import { RequirePermissions, Permissions } from '@/core/permissions/export';
import { SearchContext, SearchProvider, Search, SearchResults } from '@/core/search/export';
import '@/shared/theme/navbar-buttons.scss';
import styles from './mobile-topbar.module.css';

// A reasonable guess for the very first paint, before ResizeObserver reports
// the real measured height (see below) — never relied on after that.
export const MOBILE_TOPBAR_FALLBACK_HEIGHT_PX = 60;

const SQUARE_BUTTON_CLASS = 'btn btn-outline-primary-500 btn-sm navbar-btn navbar-btn-square';

function MobileTopbarRow({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { search } = useContext(SearchContext)!;
  const [active, setActive] = useState(false);
  const expanded = active || search !== '';

  return (
    <>
      {/* eslint-disable-next-line */}
      <a href="#" style={{ flexShrink: 0 }}>
        <img alt="logo" src={getBrandLogo()} height={'24'} />
      </a>
      <div className={styles.logoRowActions}>
        <div className={styles.searchWrapper}>
          <Search
            fullWidth
            height={36}
            onFocus={() => setActive(true)}
            onClose={() => setActive(false)}
          />
        </div>
        <div
          className={`${styles.logoRowActionsGroup}${
            expanded ? ` ${styles.logoRowActionsGroupHidden}` : ''
          }`}
        >
          {process.env.REACT_APP_TEST_ENV === 'true' ? (
            <NotificationBell />
          ) : (
            <RequirePermissions allow={Permissions.NOTIFICATIONS}>
              <NotificationBell />
            </RequirePermissions>
          )}
          <button
            type="button"
            aria-label="Open menu"
            title="Menu"
            className={SQUARE_BUTTON_CLASS}
            onClick={onOpenMenu}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </div>
    </>
  );
}

export default function MobileTopbar({
  onOpenMenu,
  onHeightChange,
}: {
  onOpenMenu: () => void;
  /** Called with the topbar's real rendered height whenever it changes, so a fixed sibling (the widget content) can pad itself by exactly that much instead of a guessed constant. */
  onHeightChange?: (px: number) => void;
}) {
  const topbarRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(MOBILE_TOPBAR_FALLBACK_HEIGHT_PX);

  useLayoutEffect(() => {
    const el = topbarRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const measured = Math.round(entry.contentRect.height);
      setHeight(measured);
      onHeightChange?.(measured);
    });
    observer.observe(el);

    return () => observer.disconnect();
    // eslint-disable-next-line
  }, []);

  return (
    <SearchProvider filterType={isAlphaFlashHost() ? 'event' : undefined}>
      <div
        className={styles.wrapper}
        style={{ '--search-results-top': `${height}px` } as React.CSSProperties}
      >
        <div ref={topbarRef} className={styles.topbar}>
          <div className={styles.logoRow}>
            <MobileTopbarRow onOpenMenu={onOpenMenu} />
          </div>
          <div className={styles.divider} />
        </div>
        <SearchResults />
      </div>
    </SearchProvider>
  );
}
